import {
  CategoricalFilter,
  Dashboard,
  DataValue,
  FilterUpdateType,
  TableauViz,
  Worksheet,
} from "@tableau/embedding-api";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { formatDateToISO } from "../../../../utils/date";
import {
  DATE_RANGE_MAPPINGS,
  TABLEAU_DATE_FILTER,
  TABLEAU_FILTERS,
  TABLEAU_PARAMETERS,
} from "../tableauCustomData";

export interface FilterOption {
  value: string;
  formattedValue: string;
  checked: boolean;
}

export interface FilterData {
  category: string;
  title: string;
  field: string;
  options: FilterOption[];
}

type SelectedItem = {
  value: any;
};

// Persistent cache for domain values
const domainCache: Record<string, DataValue[]> = {};

export const useTableauFilters = (viz: TableauViz, reportAlias: string) => {
  const [filters, setFilters] = useState<Record<string, FilterData>>({});
  const [parameters, setParameters] = useState<any>({});
  const [filtersLoaded, setFiltersLoaded] = useState(false);
  const [parametersLoaded, setParametersLoaded] = useState(false);
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [selectedDateOption, setSelectedDateOption] = useState("Today");
  const [dateRangeSelected, setDateRangeSelected] = useState(false);

  useEffect(() => {
    setFilters({});
    setParameters({});
  }, [reportAlias]);

  const initFilters = async (): Promise<void> => {
    try {
      console.time("initFilters");
      console.log("Initializing filters");

      const sheet = viz.workbook.activeSheet as Dashboard;
      const filterSheetName = "Filters";

      console.time("Find worksheet");
      const worksheet = sheet.worksheets.find(
        (ws) => ws.name === filterSheetName
      );
      console.timeEnd("Find worksheet");

      if (!worksheet) {
        toast.error(`Worksheet "${filterSheetName}" not found.`);
        return;
      }

      console.time("Check tableauFilters");
      const tableauFilters = TABLEAU_FILTERS[reportAlias];
      console.timeEnd("Check tableauFilters");

      if (!tableauFilters) {
        toast.error(
          `No filters configuration found for report alias "${reportAlias}".`
        );
        return;
      }

      console.time("Get filters from worksheet");
      const filterData = await worksheet.getFiltersAsync();
      console.timeEnd("Get filters from worksheet");

      console.log(filterData);

      const newFilters: Record<string, FilterData> = {};
      const allFilters: Record<string, DataValue[]> = {};

      console.time("Process filters");
      await Promise.all(
        filterData.map(async (filter) => {
          if (filter.filterType !== "categorical") return;

          const field = filter.fieldName;
          const key = `${field.replace(/ /g, "_").toLowerCase()}`;

          if (!tableauFilters.hasOwnProperty(key)) return;

          const categoricalFilter = filter as CategoricalFilter;

          console.time(`Get applied values for ${key}`);
          const appliedFilterValues = categoricalFilter.appliedValues.filter(
            (x) => x.value !== null
          );
          console.timeEnd(`Get applied values for ${key}`);

          // Use cached domain if available
          if (!domainCache[key]) {
            console.time(`Get domain for ${key}`);
            const domain = await categoricalFilter.getDomainAsync();
            domainCache[key] = domain.values; // Cache the domain values
            console.timeEnd(`Get domain for ${key}`);
          }

          allFilters[key] = domainCache[key];

          newFilters[key] = {
            field,
            title: tableauFilters[key].title,
            category: tableauFilters[key].category,
            options: domainCache[key].map((value) => ({
              value: value.value,
              formattedValue: value.formattedValue || "",
              checked: appliedFilterValues.some((v) => v.value === value.value),
            })),
          };
        })
      );
      console.timeEnd("Process filters");

      console.time("Set filters state");
      setFilters(newFilters);
      setFiltersLoaded(true);
      console.timeEnd("Set filters state");

      console.timeEnd("initFilters");
    } catch (error) {
      console.error("Error initializing filters:", error);
      setFiltersLoaded(false);
    }
  };

  const applyFilter = (
    field: string,
    key: string,
    selectedItems: SelectedItem[]
  ): void => {
    const selectedValues: any[] = selectedItems.map((item) => item.value);

    console.log("Applying filter:", field, "with values:", selectedValues);

    if (!viz) {
      console.log("viz is not available");
      return;
    }

    const worksheet = viz.workbook.activeSheet as Worksheet;

    if (worksheet && typeof worksheet.applyFilterAsync === "function") {
      worksheet.applyFilterAsync(
        field,
        selectedValues,
        FilterUpdateType.Replace,
        { isExcludeMode: false }
      );
    }
  };

  const initParams = async () => {
    console.time("initParams");
    console.log("Initializing parameters");

    try {
      const parametersData = await viz.workbook.getParametersAsync();
      const tableauParams = TABLEAU_PARAMETERS[reportAlias];

      if (!tableauParams) {
        console.warn(
          `No parameter configuration found for report alias "${reportAlias}".`
        );
        setParameters({});
        setParametersLoaded(false);
        return;
      }

      const newParameters = parametersData.reduce((acc, parameter) => {
        const field = parameter.name;
        let key = field.replace(/ /g, "_").replace(/\//g, "_").toLowerCase();

        const allowableValues =
          parameter.allowableValues?.allowableValues || [];
        const currentValue = parameter.currentValue;

        if (allowableValues.length === 0 || !tableauParams.hasOwnProperty(key))
          return acc;

        acc[key] = {
          field,
          title: tableauParams[key].title,
          category: tableauParams[key].category || "",
          options: allowableValues
            .filter((x) => x.value !== null)
            .map((value) => ({
              value: value.value,
              formattedValue: value.formattedValue || "",
              checked: value.value === currentValue.value,
            })),
        };

        return acc;
      }, {} as Record<string, any>);

      const sortedParameters = Object.keys(newParameters)
        .sort()
        .reduce((sortedObj, key) => {
          sortedObj[key] = newParameters[key];
          return sortedObj;
        }, {} as Record<string, any>);

      setParameters(sortedParameters);
      setParametersLoaded(true);
    } catch (error) {
      console.error("Error initializing parameters:", error);
      setParametersLoaded(false);
    } finally {
      console.timeEnd("initParams");
    }
  };

  const applyParameter = (paramName: string, newValue: any): void => {
    viz.workbook
      .changeParameterValueAsync(paramName, newValue)
      .then(() => {
        initParams();
      })
      .catch((error: Error) => {
        console.error("Error changing parameter:", error);
      });
  };

  const applyDateFilter = (dates: Date[]) => {
    if (dates.length > 1) {
      const [startDate, endDate] = dates;
      const activeSheet = viz.workbook.activeSheet as Dashboard;
      const worksheets = activeSheet.worksheets;
      const worksheet = worksheets[activeSheet.index];

      if (worksheet && typeof worksheet.applyRangeFilterAsync === "function") {
        const dateFilter = TABLEAU_DATE_FILTER[reportAlias];
        worksheet.applyRangeFilterAsync(dateFilter, {
          min: startDate,
          max: endDate,
        });

        initFilters();
      } else {
        console.error(
          "Invalid worksheet or applyRangeFilterAsync method not available."
        );
      }
    }
  };

  const applyRelativeDateFilter = (relativeDate: string) => {
    setSelectedDateOption(relativeDate);
    setDateRangeSelected(false);

    const dateRangeFunction = DATE_RANGE_MAPPINGS[relativeDate];
    if (!dateRangeFunction) return;

    const today = new Date();
    const { startDate, endDate } = dateRangeFunction(today);

    setSelectedDates([startDate, endDate]);
  };

  useEffect(() => {
    if (selectedDates.length === 2) {
      applyDateFilter(selectedDates);

      let inputElement = document.getElementById(
        "datePicker"
      ) as HTMLInputElement | null;

      if (!inputElement) return;

      const formattedDateRange = `${formatDateToISO(
        selectedDates[0]
      )} ~ ${formatDateToISO(selectedDates[1])}`;

      inputElement.value = formattedDateRange;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDates]);

  return {
    filters,
    parameters,
    filtersLoaded,
    parametersLoaded,
    initFilters,
    initParams,
    applyFilter,
    applyParameter,
    applyDateFilter,
    selectedDates,
    setSelectedDates,
    selectedDateOption,
    dateRangeSelected,
    applyRelativeDateFilter,
    setDateRangeSelected,
  };
};
